import Guard from 'arg-guard'

import Logger from '@/logger'
import PlanBuilderManager from './index'
import ServiceFactory from '@/services/service-factory'
import StatusPoller from '@/helpers/status-poller'

import Store from '@/store'
import { MUTATIONS } from '@/store/mutations'

import AppError from '@/models/app-error'
import EnsureOperationResult from '@/models/ensure-operation-result'

const loggingCategory = 'Improvements'
const loggingCategoryFlow = 'Renovation Lifecycle'

/*
 * The Plan Builder Manager uses these methods to interact with the API.
 */
const PlanBuilderApiData = {

    /**
     * Trigger the calculation on the server of improvements for an address.
     * @param {number} addressId The address identifier.
     * @returns {Promise<EnsureOperationResult>} The API result data.
     */
    async ensureInitiativesAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')
        let operationState

        try {
            const service = ServiceFactory.improvement()
            operationState = await service.ensureCalculationsAsync(addressId)

            if (!operationState.succeeded)
                operationState.throw()
            else
                return new EnsureOperationResult(operationState.data)
        } catch (err) {
            Logger.logObject(operationState, `Unable to confirm improvement calculations for the property with ID ${ addressId }: ${ err.message }`, loggingCategory)
            if (addressId !== 0) {
                Store.commit(MUTATIONS.logAppError, new AppError(`Unable to confirm improvement calculations for the property with ID ${ addressId }`, true))
                throw err
            }
        }
    },

    /**
     * Loads the current project improvements data.
     * @param {number} addressId The address identifier.
     * @returns {Promise<Object>} Th API result data.
     */
    async loadInitiativesAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')
        let operationState

        try {
            const service = ServiceFactory.improvement()
            operationState = await service.loadImprovementsAsync(addressId)

            if (!operationState.succeeded)
                operationState.throw()
            else
                return operationState.data
        } catch (err) {
            Logger.logObject(operationState, `Unable to load data for the property with ID ${ addressId }: ${ err.message }`, loggingCategory)
            if (addressId !== 0) {
                Store.commit(MUTATIONS.logAppError, new AppError(`Unable to load improvements for the property with ID ${ addressId }`, true))
                throw err
            }
        }
    },

    /**
     * Determines if local data is out of sync with the server so project state can be invalidated.
     * @param {number} addressId The address identifier.
     * @param {string|Date?} dateStamp The date stamp from the current local version of the data.
     * @returns {Promise<boolean>} The API result data.
     */
    async isLocalDataOutOfSyncAsync(addressId, dateStamp) {
        Guard.throwIf(addressId, 'addressId')

        if (!dateStamp) return true

        let operationState

        try {
            const service = ServiceFactory.improvement()
            operationState = await service.localDataOutOfSyncAsync(addressId, dateStamp)

            if (!operationState.succeeded)
                operationState.throw()
            else
                return operationState.data
        } catch (err) {
            Logger.logObject(operationState, `Unable to check if data in sync for the property with ID ${ addressId }: ${ err.message }`, loggingCategory)
            Logger.error(err)

            // Failing to return false here will make a bad situation much worse.
            return false
        }
    },

    /**
     * Loads the initial savings data associated with an address into state.
     * @param {number} addressId The address identifier.
     * @returns {Promise<Object>} The results.
     */
    async loadInitialSavingsAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')
        let operationState

        try {
            const service = ServiceFactory.saving()
            operationState = await service.loadInitialSavingsAsync(addressId)

            if (!operationState.succeeded)
                operationState.throw()
            else
                return operationState.data
        } catch (err) {
            Logger.logObject(operationState, `Unable to load data for the property with ID ${ addressId }: ${ err.message }`, loggingCategory)
            if (addressId !== 0) {
                Store.commit(MUTATIONS.logAppError, new AppError(`Unable to load initial savings for the property with ID ${ addressId }`, true))
                throw err
            }
        }
    },

    /**
     * Loads specific sets of savings data for initiative group sets into state to append to the initial savings data.
     * @param {number} addressId The address identifier.
     * @param {Array<number>} improvementIds The IDs of the Initiative Groups to include.
     * @returns {Promise<Object>} The results.
     */
    async loadSavingsForInitiativeGroupSetAsync(addressId, improvementIds) {
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(improvementIds, 'improvementIds')
        let operationState

        try {
            const service = ServiceFactory.saving()
            operationState = await service.loadSavingsAsync(addressId, improvementIds)

            if (!operationState.succeeded)
                operationState.throw()
            else
                return operationState.data
        } catch (err) {
            const failedIds = improvementIds.sort((a, b) => a - b)
            Logger.logObject(operationState, `Unable to load data for the property with ID ${ addressId }: ${ err.message }`, loggingCategory)
            Store.commit(MUTATIONS.logAppError, new AppError(`Unable to load savings for the property with ID ${ addressId } for the following improvements (${ failedIds.join(', ') })`, true))
            throw err
        }
    },

    /**
     * Polls to check the state of the improvements calculations, waiting to reload them.
     * @param {number} addressId The address identifier.
     * @returns {Promise<ServiceOperation>}
     */
    async pollCalculationStatusAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        Logger.logObject({ addressId }, 'Polling calculation status', loggingCategoryFlow)
        return await StatusPoller.executeAfterPollAsync(`calculations for the property with ID ${ addressId }`, 'checkAnalysisCalculationAsync', async() => {
            Logger.logObject({ addressId }, 'Calculation complete - Ready to load project', loggingCategoryFlow)
            await PlanBuilderManager.loadProjectIntoStoreAsync(addressId, { force: true, initialise: true })
            Store.commit(MUTATIONS.setPropertyCalculationComplete)
        }, addressId)
    },

    /**
     * Sends a System Generated error report.
     * @param {number} schemeId The scheme to use for reporting errors to.
     * @param {string} errorMessage The error message to send.
     * @returns {Promise<Object>} The returned operation data.
     */
    async sendSystemGeneratedErrorReportAsync(schemeId, errorMessage) {
        Guard.throwIf(errorMessage, 'errorMessage')
        let operationState

        try {
            const error = new AppError(`SYSTEM GENERATED ERROR REPORT: Do Not contact the user - ${ errorMessage }`)
            const service = ServiceFactory.contact()
            operationState = await service.reportErrorAsync(schemeId, error)

            if (!operationState.succeeded)
                operationState.throw()
            else
                return operationState.data
        } catch (err) {
            Logger.error(errorMessage)
            Logger.error(`Unable to report the above error: ${ err }`)
        }
    },
}

export default PlanBuilderApiData
